import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, buttonClasses, Card, MicroLabel } from "@bht/ui";
import { fetchHiddenTestToday, type HtsToday } from "../lib/api.js";

/**
 * Home-screen card that surfaces the daily Hidden Test Score ritual.
 *
 * Three states:
 *   - Loading      → null (silent; the rest of /home renders)
 *   - Locked       → not shown (user needs more attempts; the home greeting
 *                   already covers onboarding)
 *   - Open today   → call-to-action card
 *   - Done today   → score recap card with link to revisit
 */
export function HiddenTestCard() {
  const [data, setData] = useState<HtsToday | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchHiddenTestToday()
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) return null;
  if (!data.unlocked) return null;

  if (data.completed) {
    return (
      <Card>
        <div className="app-stack" style={{ gap: "var(--sp-2)" }}>
          <MicroLabel color="accent">Today's Hidden Test Score</MicroLabel>
          <div style={{ display: "flex", alignItems: "baseline", gap: "var(--sp-3)" }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontVariantNumeric: "tabular-nums",
                fontSize: 56,
                fontWeight: 600,
                color: "var(--fg-0)",
                lineHeight: 1,
                letterSpacing: "-0.02em",
              }}
            >
              {data.score.total}
            </span>
            <span style={{ color: "var(--fg-3)", fontSize: "var(--t-small)" }}>
              out of 100
            </span>
          </div>
          <p style={{ color: "var(--fg-2)", fontSize: "var(--t-small)", margin: 0 }}>
            Done for today. Comes back tomorrow with a fresh 5-item set.
          </p>
          <Link to="/app/hidden-test" style={{ alignSelf: "stretch" }} className={buttonClasses({ variant: "ghost", full: true })}>Review today's score</Link>
        </div>
      </Card>
    );
  }

  // Open: ready to take
  return (
    <Card armed>
      <div className="app-stack" style={{ gap: "var(--sp-2)" }}>
        <MicroLabel color="accent">Daily Hidden Test Score</MicroLabel>
        <h2
          style={{
            color: "var(--fg-0)",
            fontFamily: "var(--font-sans)",
            fontSize: "var(--t-h1)",
            fontWeight: 600,
            lineHeight: 1.3,
            margin: 0,
          }}
        >
          5 items, about 3 minutes.
        </h2>
        <p style={{ color: "var(--fg-2)", fontSize: "var(--t-body)", lineHeight: 1.55 }}>
          One item from each module, weighted to your weakest skills. Scored
          0 to 100 from correctness, trap-pattern credit, and answer speed.
        </p>
        {/* Secondary, not primary. Home's recommended drill is the lead action;
            the daily set is the second option, so only one solid amber button
            competes for the student's first tap (2026-07-28 design reviews). */}
        <Link to="/app/hidden-test" style={{ alignSelf: "stretch" }} className={buttonClasses({ variant: "secondary", full: true })}>Start today's set</Link>
      </div>
    </Card>
  );
}
